/**
 * XRAssetLibrary — v10.11.0
 * Tab admin dentro de `XRAdminPage` para subir modelos GLB/glTF al Object
 * Storage y vincularlos a marcadores XR. Incluye preview 3D con Three.js.
 *
 * Endpoints usados:
 *  - POST /xr/assets/upload  (multipart)
 *  - GET  /xr/assets         (listar)
 *  - GET  /xr/assets/{id}/file?auth=  (descarga con query auth)
 *  - POST /xr/markers/bind   (vincular)
 *  - GET  /xr/markers        (listar para dropdown)
 */
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import api from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Cube, UploadSimple, Link as LinkIcon, Trash, Eye, X } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function XRAssetLibrary() {
  const [assets, setAssets] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewAsset, setPreviewAsset] = useState(null);
  const [bindingAsset, setBindingAsset] = useState(null);
  const [uploadForm, setUploadForm] = useState({ name: '', asset_type: '3d_model' });
  const [uploadingProgress, setUploadingProgress] = useState(0);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, m] = await Promise.all([
        api.get('/xr/assets'),
        api.get('/xr/markers'),
      ]);
      setAssets(a.data?.assets || []);
      setMarkers(m.data?.markers || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error cargando biblioteca');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploadingProgress(1);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('name', uploadForm.name || file.name);
    fd.append('asset_type', uploadForm.asset_type);
    try {
      await api.post('/xr/assets/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setUploadingProgress(Math.round(100 * e.loaded / e.total));
        },
      });
      toast.success('Asset subido correctamente');
      setUploadForm({ name: '', asset_type: '3d_model' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al subir');
    } finally { setUploadingProgress(0); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este asset? (el archivo permanece en storage)')) return;
    try {
      await api.delete(`/xr/assets/${id}`);
      toast.success('Asset eliminado');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  return (
    <Card data-testid="xr-asset-library">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2"><Cube size={18} weight="fill" className="text-cyan-600" /> Biblioteca 3D</CardTitle>
          <Badge className="bg-cyan-100 text-cyan-700">{assets.length} assets · {markers.filter((m) => m.asset_id).length} vínculos</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Upload zone */}
        <div className="rounded-lg border-2 border-dashed border-cyan-300 bg-cyan-50/50 p-4 space-y-3" data-testid="xr-upload-zone">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
            <div>
              <Label className="text-xs">Nombre (opcional)</Label>
              <Input value={uploadForm.name} onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })} placeholder="Corazón anatómico" data-testid="upload-name" />
            </div>
            <div>
              <Label className="text-xs">Tipo</Label>
              <Select value={uploadForm.asset_type} onValueChange={(v) => setUploadForm({ ...uploadForm, asset_type: v })}>
                <SelectTrigger data-testid="upload-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="3d_model">Modelo 3D (.glb/.gltf)</SelectItem>
                  <SelectItem value="image">Imagen (.png/.jpg)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Archivo</Label>
              <Input
                ref={fileInputRef}
                type="file"
                accept={uploadForm.asset_type === '3d_model' ? '.glb,.gltf' : '.png,.jpg,.jpeg,.webp'}
                onChange={(e) => handleUpload(e.target.files?.[0])}
                data-testid="upload-file-input"
                disabled={uploadingProgress > 0}
              />
            </div>
          </div>
          {uploadingProgress > 0 && (
            <div className="space-y-1" data-testid="upload-progress">
              <div className="flex justify-between text-xs">
                <span className="text-cyan-700">Subiendo…</span>
                <span className="text-cyan-700 font-mono">{uploadingProgress}%</span>
              </div>
              <div className="w-full h-1.5 bg-cyan-100 rounded-full overflow-hidden">
                <div className="h-full bg-cyan-500 transition-all" style={{ width: `${uploadingProgress}%` }} />
              </div>
            </div>
          )}
          <div className="text-[11px] text-slate-500 flex items-center gap-1">
            <UploadSimple size={12} /> Formatos: GLB (preferido, texturas embebidas), glTF · Máximo 25 MB
          </div>
        </div>

        {/* Assets list */}
        {loading ? (
          <div className="text-center py-8 text-slate-400">Cargando…</div>
        ) : assets.length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-sm">Sin assets. Sube un modelo GLB arriba.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {assets.map((a) => {
              const bound = markers.filter((m) => m.asset_id === a.id);
              return (
                <div key={a.id} className="p-3 rounded-lg border border-slate-200 bg-white flex items-start gap-3" data-testid={`asset-row-${a.id}`}>
                  <div className="w-10 h-10 rounded bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center shrink-0">
                    <Cube size={20} weight="fill" className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{a.name}</div>
                    <div className="text-[11px] text-slate-500">
                      {a.format?.toUpperCase()} · {((a.size_bytes || 0) / 1024).toFixed(1)} KB · v{a.version || 1}
                    </div>
                    {bound.length > 0 && (
                      <div className="text-[10px] text-emerald-600 mt-0.5 truncate">
                        ↳ vinculado a: {bound.map((m) => m.marker_id).join(', ')}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    {a.asset_type === '3d_model' && (
                      <Button size="icon" variant="ghost" onClick={() => setPreviewAsset(a)} className="h-7 w-7" data-testid={`preview-${a.id}`} title="Vista previa 3D">
                        <Eye size={14} />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => setBindingAsset(a)} className="h-7 w-7 text-cyan-600" data-testid={`bind-${a.id}`} title="Vincular a marcador">
                      <LinkIcon size={14} />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => handleDelete(a.id)} className="h-7 w-7 text-red-600" data-testid={`delete-${a.id}`} title="Eliminar">
                      <Trash size={14} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {previewAsset && <Asset3DPreviewModal asset={previewAsset} onClose={() => setPreviewAsset(null)} />}
        {bindingAsset && <MarkerBindingModal asset={bindingAsset} markers={markers} onClose={() => setBindingAsset(null)} onSaved={load} />}
      </CardContent>
    </Card>
  );
}

/* ============ Preview modal con Three.js ============ */
function Asset3DPreviewModal({ asset, onClose }) {
  const canvasRef = useRef(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    let stopped = false;
    let raf;
    let renderer;
    (async () => {
      try {
        const THREE = await import('three');
        const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
        const canvas = canvasRef.current;
        if (!canvas || !alive) return;
        const width = canvas.clientWidth || 400;
        const height = canvas.clientHeight || 360;
        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0x0f172a);
        const camera = new THREE.PerspectiveCamera(45, width / height, 0.01, 100);
        camera.position.set(0, 0.5, 2);
        renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
        renderer.setSize(width, height, false);
        scene.add(new THREE.HemisphereLight(0xffffff, 0x1e293b, 1.5));
        const dir = new THREE.DirectionalLight(0x67e8f9, 0.8);
        dir.position.set(3, 5, 2);
        scene.add(dir);

        const token = localStorage.getItem('token') || '';
        const url = `${BACKEND_URL}/api/xr/assets/${asset.id}/file?auth=${encodeURIComponent(token)}`;
        const loader = new GLTFLoader();
        loader.load(
          url,
          (gltf) => {
            if (!alive || stopped) return;
            const model = gltf.scene;
            const box = new THREE.Box3().setFromObject(model);
            const size = box.getSize(new THREE.Vector3()).length();
            const center = box.getCenter(new THREE.Vector3());
            model.position.sub(center);
            const dist = Math.max(size * 1.2, 0.5);
            camera.position.set(0, size * 0.3, dist);
            camera.lookAt(0, 0, 0);
            scene.add(model);
            setLoading(false);
            let t = 0;
            const animate = () => {
              if (stopped) return;
              t += 0.008;
              model.rotation.y = t;
              try { renderer.render(scene, camera); } catch { /* disposed */ }
              raf = requestAnimationFrame(animate);
            };
            raf = requestAnimationFrame(animate);
          },
          undefined,
          (e) => {
            if (!alive) return;
            setErr(e?.message || 'No se pudo cargar el modelo');
            setLoading(false);
          },
        );
      } catch (e) {
        setErr(e?.message || 'Error inicializando Three.js');
        setLoading(false);
      }
    })();
    return () => {
      alive = false;
      stopped = true;
      cancelAnimationFrame(raf);
      try { renderer?.dispose?.(); } catch { /* noop */ }
    };
  }, [asset.id]);

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="asset-preview-modal">
      <div className="w-full max-w-lg bg-slate-900 rounded-xl overflow-hidden border border-cyan-800">
        <div className="p-3 border-b border-slate-800 flex items-center justify-between">
          <div>
            <div className="text-sm font-bold text-white">{asset.name}</div>
            <div className="text-[10px] text-slate-400 font-mono">{asset.format?.toUpperCase()} · {((asset.size_bytes || 0) / 1024).toFixed(1)} KB</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1" data-testid="preview-close-btn"><X size={18} /></button>
        </div>
        <div className="relative bg-slate-900" data-testid="preview-canvas-container">
          <canvas ref={canvasRef} className="w-full block" style={{ height: 360 }} />
          {loading && !err && <div className="absolute inset-0 flex items-center justify-center text-cyan-400 pointer-events-none">Cargando modelo…</div>}
          {err && <div className="absolute inset-0 flex items-center justify-center text-red-400 text-sm p-4 text-center pointer-events-none">{err}</div>}
        </div>
        <div className="p-3 border-t border-slate-800 text-[10px] text-slate-500 text-center">
          Preview con rotación automática · Se renderizará igual sobre el marcador vinculado en el player XR
        </div>
      </div>
    </div>
  );
}

/* ============ Modal de vinculación con marcador ============ */
function MarkerBindingModal({ asset, markers, onClose, onSaved }) {
  const preSelected = markers.find((m) => m.asset_id === asset.id);
  const [markerId, setMarkerId] = useState(preSelected?.marker_id || (markers[0]?.marker_id || ''));
  const [scale, setScale] = useState(preSelected?.asset_scale || 1);
  const [offsetY, setOffsetY] = useState(preSelected?.asset_offset?.y || 0);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!markerId) return;
    setSaving(true);
    try {
      await api.post('/xr/markers/bind', {
        marker_id: markerId,
        asset_id: asset.id,
        scenario_id: null,
        scale: Number(scale) || 1,
        offset: { x: 0, y: Number(offsetY) || 0, z: 0 },
      });
      toast.success('Vinculado');
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally { setSaving(false); }
  };

  const unbind = async () => {
    if (!preSelected) return onClose();
    setSaving(true);
    try {
      await api.post('/xr/markers/bind', {
        marker_id: preSelected.marker_id,
        asset_id: null,
        scenario_id: null,
      });
      toast.success('Desvinculado');
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="marker-bind-modal">
      <div className="w-full max-w-md bg-white rounded-xl overflow-hidden shadow-xl">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <div className="text-sm font-bold text-slate-900">Vincular a marcador</div>
            <div className="text-[11px] text-slate-500">{asset.name}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900" data-testid="bind-close-btn"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <Label className="text-xs">Marcador</Label>
            <Select value={markerId} onValueChange={setMarkerId}>
              <SelectTrigger data-testid="bind-marker-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {markers.map((m) => (
                  <SelectItem key={m.id} value={m.marker_id}>
                    {m.marker_id} — {m.label || m.marker_type}
                    {m.asset_id && m.asset_id !== asset.id && ' (ya vinculado a otro)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Escala</Label>
              <Input type="number" step="0.1" min="0.1" max="10" value={scale} onChange={(e) => setScale(e.target.value)} data-testid="bind-scale-input" />
            </div>
            <div>
              <Label className="text-xs">Offset Y (m)</Label>
              <Input type="number" step="0.05" value={offsetY} onChange={(e) => setOffsetY(e.target.value)} data-testid="bind-offset-y-input" />
            </div>
          </div>
        </div>
        <div className="p-4 border-t flex items-center justify-between gap-2">
          {preSelected ? (
            <Button variant="outline" size="sm" onClick={unbind} disabled={saving} className="text-red-600" data-testid="bind-unbind-btn">Desvincular</Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancelar</Button>
            <Button size="sm" onClick={save} disabled={saving || !markerId} className="bg-cyan-600 hover:bg-cyan-500" data-testid="bind-save-btn">
              {saving ? 'Guardando…' : 'Vincular'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
